import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

/* ===== Images ===== */
import logo from "../images/iitrprlogo.png";
import tbiflogo from "../global-images/tbif-iit-ropar.png";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Scroll effect for navbar shadow/glassmorphism
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Nav Items Configuration
  const navItems = [
    { name: "Home", path: "/" },
    {
      name: "About",
      path: "/about",
      dropdown: [
        { name: "About Us", path: "/about" },
        { name: "Team", path: "/team" },
        { name: "Success Stories", path: "/success-stories" },
      ],
    },
    {
      name: "Support",
      path: "/facilities",
      dropdown: [
        { name: "Incubator Facilities", path: "/facilities" },
        { name: "Mentors & SMEs", path: "/mentors" },
        { name: "Zoho for Startups", path: "/zoho-for-startups" },
      ],
    },
    {
      name: "Portfolio",
      path: "/portfolio",
      dropdown: [
        { name: "Incubated Startups", path: "/portfolio" },
        { name: "Defence Startups", path: "/defence" },
        { name: "Partners", path: "/mous" },
      ],
    },
    { name: "Events", path: "/events" },
    { name: "Careers", path: "/career" },
    { name: "School Of Entrepreneurship", path: "/SchoolOfEntrepreneurship" },
    { name: "Contact", path: "/contact-us" },
  ];

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isOpen
          ? "bg-white py-4 border-b border-slate-200"
          :scrolled
          ? "bg-white/95 backdrop-blur-md shadow-md py-2 border-b border-slate-200"
          : "bg-white py-4 border-b border-transparent shadow-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center relative z-50 bg-white">
        
        {/* ===== Logo Section ===== */}
        <div className="flex items-center gap-4">
          <a href="https://iitrpr.ac.in" target="_blank" rel="noreferrer">
            <img src={logo} alt="IIT Ropar" className="h-12 w-auto object-contain" />
          </a>
          <div className="h-8 w-px bg-slate-300 hidden sm:block"></div>
          <Link to="/" onClick={() => window.scrollTo(0, 0)}>
            <img src={tbiflogo} alt="TBIF" className="h-10 w-auto object-contain" />
          </Link>
        </div>

        {/* ===== Desktop Navigation ===== */}
        <div className="hidden lg:flex items-center gap-6">
          {navItems.map((item, idx) => (
            <div key={idx} className="relative group">
              {item.dropdown ? (
                // Dropdown Trigger
                <div className="flex items-center gap-1 cursor-pointer text-slate-700 font-bold text-sm hover:text-blue-600 transition-colors py-2">
                  <span>{item.name}</span>
                  <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-200" />
                  
                  {/* Dropdown Menu */}
                  <div className="absolute top-full left-0 pt-4 w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-2 group-hover:translate-y-0">
                    <div className="bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden">
                        {item.dropdown.map((subItem, subIdx) => (
                            <Link
                                key={subIdx}
                                to={subItem.path}
                                onClick={() => window.scrollTo(0, 0)}
                                className="block px-4 py-3 text-sm font-medium text-slate-600 hover:bg-blue-50 hover:text-blue-600 transition-colors border-b border-slate-50 last:border-none"
                            >
                                {subItem.name}
                            </Link>
                        ))}
                    </div>
                  </div>
                </div>
              ) : (
                // Standard Link
                <Link
                  to={item.path}
                  onClick={() => window.scrollTo(0, 0)}
                  className="text-slate-700 font-bold text-sm hover:text-blue-600 transition-colors relative"
                >
                  {item.name}
                </Link>
              )}
            </div>
          ))}

          {/* CTA Button */}
          <a
            href="https://forms.gle/Fz2pn47EqTscZK4T8"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold px-6 py-2.5 rounded-full shadow-lg hover:shadow-blue-500/30 transition-all transform hover:-translate-y-0.5"
          >
            Incubate!
          </a>
        </div>

        {/* ===== Mobile Menu Button ===== */}
        <button
          className="lg:hidden text-slate-700 hover:text-blue-600"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* ===== Mobile Dropdown Menu ===== */}
      <div
        className={`fixed inset-0 z-40 bg-white transform transition-transform duration-300 lg:hidden flex flex-col pt-24 px-6 overflow-y-auto ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col gap-4">
          {navItems.map((item, idx) => (
            <div key={idx} className="border-b border-slate-100 pb-4">
              {item.dropdown ? (
                <div className="flex flex-col">
                  <span className="text-lg font-bold text-slate-900 mb-2">{item.name}</span>
                  <div className="flex flex-col gap-2 pl-4 border-l-2 border-blue-100">
                    {item.dropdown.map((subItem, subIdx) => (
                      <Link
                        key={subIdx}
                        to={subItem.path}
                        className="text-slate-600 font-medium py-1"
                        onClick={() => {
                            setIsOpen(false);
                            window.scrollTo(0, 0);
                        }}
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  to={item.path}
                  className="text-lg font-bold text-slate-900 block"
                  onClick={() => {
                    setIsOpen(false);
                    window.scrollTo(0, 0);
                  }}
                >
                  {item.name}
                </Link>
              )}
            </div>
          ))}
          
          <a
            href="https://forms.gle/Fz2pn47EqTscZK4T8"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 bg-blue-600 text-white text-center font-bold py-4 rounded-xl text-lg shadow-lg"
          >
            Apply for Incubation
          </a>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;